import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import {
    DateFilterModel,
    FilterChangedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    NumberFilterModel,
    PaginationChangedEvent,
    SortChangedEvent,
    TextFilterModel
} from "ag-grid-community";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { GridService } from "../../services/grid.service";
import { CustomDropdownFilterModel } from "./dropdown-filter/dropdown-filter.model";
import { GridContextParams } from "./grid-context.model";
import { GridData } from "./grid-data.model";
import { FilterCriteria, FilterOptions } from "./grid-filter.model";
import { GridPagingOptions } from "./grid-query.model";
import { SortCriteria, SortOptions } from "./grid-sort.model";

export const ACTIONS_COL_ID = "actionsCol";

@Component({
    selector: "kno2-grid",
    templateUrl: "./grid.component.html",
    styleUrls: [],
    standalone: false
})
export class GridComponent implements OnInit, OnDestroy {
    @Input() public data: Subject<GridData>;
    @Input() public options: GridOptions & { context?: GridContextParams };
    @Input() public frameworkComponents: any;
    @Input() public filterOptions?: FilterOptions;
    @Input() public sortOptions?: SortOptions;
    @Input() public pagingOptions?: GridPagingOptions;
    @Output() public sortChange = new EventEmitter<Array<SortCriteria>>();
    @Output() public filterChange = new EventEmitter<Array<FilterCriteria>>();
    @Output() public pageChange = new EventEmitter<GridPagingOptions>();
    @Output() public selectionChange = new EventEmitter<any>();
    private destroy$ = new Subject<void>();
    private dataGridApi: GridApi;
    private activeFilter: string;
    private activeSort: string;
    public isLoading = false;
    public rowData: any = [];

    constructor(private gridService: GridService) {}

    public ngOnInit(): void {
        if (!this.sortOptions?.replaceOnChange) this.options.multiSortKey = "ctrl";

        this.options.suppressMovableColumns = true;
        this.options.animateRows = true;
        this.options.enableCellTextSelection = true;

        this.options.theme = "legacy";
    }

    public onDataGridReady(params: GridReadyEvent): void {
        this.gridService.init(params);
        this.dataGridApi = params.api;

        this.data.pipe(takeUntil(this.destroy$)).subscribe((response) => {
            this.isLoading = response.isLoading;

            if (response.isLoading) return;

            if (!response.items.length) {
                this.rowData = [];
                return;
            }

            const previousItemCount = this.dataGridApi.paginationGetPageSize() * this.dataGridApi.paginationGetCurrentPage();
            const remainingItemCount = response.totalCount - (previousItemCount + response.items.length);

            this.rowData = new Array(previousItemCount > 0 ? previousItemCount : 0)
                .fill({})
                .concat(response.items)
                .concat(new Array(remainingItemCount > 0 ? remainingItemCount : 0).fill({}));
        });
    }

    public onFirstDataRendered(): void {
        if (this.pagingOptions?.pageNumber && this.pagingOptions?.pageNumber !== 1) this.dataGridApi.paginationGoToPage(this.pagingOptions.pageNumber - 1);
    }

    public onSelectionChange(): void {
        let result = this.dataGridApi.getSelectedRows();
        if (this.options?.rowSelection === "single") result = result[0];
        this.selectionChange.emit(result);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public handleSortChange(event: SortChangedEvent): void {
        const sortModels = Object.values(event.api.getColumnState()).filter(({ sort }) => !!sort);
        const newSortModel = sortModels.filter(({ colId }) => colId !== this.activeSort)?.[0];
        let results = sortModels;
        if (this.sortOptions?.replaceOnChange && newSortModel && this.activeSort !== newSortModel.colId) {
            const column = event.api.getColumn(this.activeSort);
            if (column) {
                event.api.applyColumnState({
                    state: [{ colId: this.activeSort, sort: null }]
                });
            }
            results = [newSortModel];
            this.activeSort = newSortModel.colId;
        }
        this.sortChange.emit(
            results?.map(({ colId, sort }) => ({
                column: colId,
                value: sort
            }))
        );
    }

    public handlePaginationChange(event: PaginationChangedEvent): void {
        if (!event.newPage) return;

        this.pageChange.emit({
            pageNumber: this.dataGridApi.paginationGetCurrentPage() + 1,
            pageSize: this.dataGridApi?.paginationGetPageSize()
        });
    }

    public handleFilterChange(event: FilterChangedEvent): void {
        const filterModels = Object.entries(event.api.getFilterModel());
        const newFilterModel = filterModels.filter((model) => model[0] !== this.activeFilter);
        let results: Array<[string, any]> = filterModels;
        if (this.filterOptions?.replaceOnChange && newFilterModel[0]?.[0] && this.activeFilter !== newFilterModel[0]?.[0]) {
            event.api.destroyFilter(this.activeFilter);
            results = newFilterModel;
            this.activeFilter = newFilterModel[0]?.[0];
        }
        this.filterChange.emit(
            results?.map((x) => ({
                column: x[0],
                value: this.getFilterValue(x[1])
            }))
        );
    }

    private getFilterValue(filter: DateFilterModel | TextFilterModel | NumberFilterModel | CustomDropdownFilterModel): string {
        const NO_FILTER_VALUE = "NO_FILTER_VALUE";
        switch (filter.filterType) {
            case "text":
                return (filter as TextFilterModel).filter || NO_FILTER_VALUE;
            case "number":
                return (filter as NumberFilterModel).filter?.toString() || NO_FILTER_VALUE;
            case "date":
                return (filter as DateFilterModel).dateFrom || NO_FILTER_VALUE;
            case "customDropdown":
                return (filter as CustomDropdownFilterModel).selected || NO_FILTER_VALUE;
            default:
                return NO_FILTER_VALUE;
        }
    }
}
